import { Injectable, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { LegacyRoutes, RETURN_URL_PARAM, ShellRoutes } from '../portal-routes';
import { PortalUrls } from '../portal-urls.service';
import { ReturnUrlStore } from '../return-url/return-url.store';
import { PortalSession } from './portal-session.service';
import { PortalSessionMarker } from './portal-session-marker';

/** Starts and ends the authentication of the portal user. */
@Injectable({ providedIn: 'root' })
export class AuthFlowService {
  private readonly oktaAuth = inject(OKTA_AUTH);
  private readonly session = inject(PortalSession);
  private readonly returnUrls = inject(ReturnUrlStore);
  private readonly urls = inject(PortalUrls);
  private readonly router = inject(Router);

  /** Okta sign-in. After the callback the user always arrives on the landing route, which decides where to go. */
  signIn(): Promise<void> {
    return this.oktaAuth.signInWithRedirect({ originalUri: ShellRoutes.landing });
  }

  /** `true` with a valid Shell session, otherwise the result of {@link requireSignIn} for the requested URL. */
  async ensureSignedIn(requestedUrl: string): Promise<true | UrlTree | false> {
    return (await this.session.isValid()) || this.requireSignIn(requestedUrl);
  }

  /**
   * Content was requested without a valid Shell session: the user is authenticated silently when a portal
   * session exists, otherwise on the welcome page, which receives the requested URL as deeplink.
   */
  async requireSignIn(requestedUrl: string): Promise<UrlTree | false> {
    const deeplink = this.returnUrls.accepts(requestedUrl) ? requestedUrl : undefined;
    if (this.session.hasActiveSession()) {
      // No welcome page in between: the deeplink is handed over to the landing route directly
      if (deeplink) {
        this.returnUrls.capture(deeplink);
      }
      await this.signIn();
      return false;
    }
    return this.router.createUrlTree([ShellRoutes.welcome], deeplink ? { queryParams: { [RETURN_URL_PARAM]: deeplink } } : {});
  }

  /** Ends the portal and the Okta session. Legacy then discards its own tokens and returns the user to the Shell welcome page. */
  signOut(): Promise<boolean> {
    PortalSessionMarker.remove();
    this.returnUrls.clear();
    return this.oktaAuth.signOut({ postLogoutRedirectUri: this.urls.legacyPortal(LegacyRoutes.signedOut) });
  }
}
